import React, { BaseSyntheticEvent } from 'react';

import { Divider, Menu, MenuItem, CircularProgress } from '@mui/material';
import { v4 as uuid } from 'uuid';

import { CARD_PRIMARY, GREYSCALE_WHITE } from '../../constants/colors';
import { FONT_ROBOTO, FONT_SIZE_14, FONT_WEIGHT_700 } from '../../constants/constants';
import { GenericVoidFunction } from '../../types/genericTypes';

export interface IMenuItem {
  label: string;
  loading?: boolean;
  action: GenericVoidFunction;
  maxHeight?: number;
  images?: any;
  iconFirst?: boolean;
}

export interface IMenuPosition {
  horizontal: number | 'center' | 'left' | 'right';
  vertical: number | 'bottom' | 'top' | 'center';
}

export const getSelectedIndex = (event: BaseSyntheticEvent<PointerEvent, HTMLLIElement, HTMLLIElement>): number => {
  const div = event.currentTarget.parentElement as HTMLDivElement;
  const ul = div.parentElement as HTMLUListElement;
  return [...ul.children].indexOf(div);
};

export interface IStyledMenuListProps {
  width?: number;
}

interface StyledMenuProps {
  anchorEl: HTMLElement | null;
  menuItems: IMenuItem[];
  handleClose: () => void;
  maxHeight?: number;
  styledMenuListProps?: IStyledMenuListProps;
  anchorOrigin?: IMenuPosition;
  transformOrigin?: IMenuPosition;
  font?: {
    family: 'Roboto' | 'Obviously';
    weight: number;
    size: string;
  };
}

const defaultMenuListProps: IStyledMenuListProps = {
  width: 250
};

const defaultAnchor: IMenuPosition = {
  vertical: 'bottom',
  horizontal: 'right'
};

const defaultTransform: IMenuPosition = {
  vertical: 'top',
  horizontal: 'right'
};

const defaultFont: StyledMenuProps['font'] = {
  family: FONT_ROBOTO as 'Roboto' | 'Obviously',
  weight: FONT_WEIGHT_700,
  size: FONT_SIZE_14
};

const getCustomStyles = (fontFamily: string, fontSize: string, fontWeight: number) => {
  return {
    paper: {
      borderRadius: '8px',
      boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.12)'
    },
    list: {
      padding: 0
    },
    menu: {
      marginTop: '4px'
    },
    menuItem: {
      display: 'flex',
      alignItems: 'center',
      minWidth: '200px',
      fontSize: fontSize,
      padding: '16px',
      fontWeight: fontWeight,
      fontFamily: fontFamily,
      backgroundColor: CARD_PRIMARY
    },
    labelFirst: {
      justifyContent: 'space-between'
    },
    labelExpanded: {
      flexGrow: 1
    },
    icon: {
      marginRight: '8px'
    },
    expandIcon: {
      marginLeft: 'auto'
    },
    customLoading: {
      margin: '2px 10px',
      color: GREYSCALE_WHITE
    },
    customMenuIcon: {
      margin: '0 8px'
    }
  };
};

const StyledMenu = ({
  anchorEl,
  handleClose,
  menuItems,
  maxHeight = 400,
  styledMenuListProps = defaultMenuListProps,
  anchorOrigin = defaultAnchor,
  transformOrigin = defaultTransform,
  font = defaultFont
}: StyledMenuProps) => {
  const muiMenuProps = { fontFamily: font.family, fontSize: font.size, fontWeight: font.weight };
  const customStyles = getCustomStyles(muiMenuProps.fontFamily, muiMenuProps.fontSize, muiMenuProps.fontWeight);
  const open = Boolean(anchorEl);

  const getMenuStyles = (iconFirst: boolean) => {
    return iconFirst ? customStyles.menuItem : { ...customStyles.menuItem, ...customStyles.labelFirst };
  };
  const getLabelStyles = (iconFirst: boolean) => {
    return iconFirst ? customStyles.labelExpanded : '';
  };
  const getIconStyles = () => {
    return customStyles.customMenuIcon;
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      sx={{ paper: customStyles.paper, list: customStyles.list, menu: customStyles.menu }}
      disableScrollLock={false}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      slotProps={{
        paper: {
          style: {
            maxHeight: `${maxHeight}px`
          }
        }
      }}
      MenuListProps={{
        disablePadding: true,
        style: {
          width: `${styledMenuListProps.width}px`
        }
      }}
    >
      {menuItems?.map((item, index) => (
        <div key={uuid()}>
          <MenuItem sx={{ ...getMenuStyles(Boolean(item.iconFirst)) }} onClick={item.action}>
            {item.iconFirst && item.images && <item.images.normal style={{ ...getIconStyles() }} />}
            <div style={{ ...getLabelStyles(Boolean(item.iconFirst)) }}>{item.label}</div>
            {item.loading && <CircularProgress sx={{ ...customStyles.customLoading }} size={20} />}
            {!item.loading && !item.iconFirst && item.images && <item.images.normal className="custom-menu-icon" />}
          </MenuItem>
          {menuItems.length !== index + 1 && <Divider />}
        </div>
      ))}
    </Menu>
  );
};

export default StyledMenu;
